/**
 * Payment hooks — TanStack Query for saved payment methods and card charges.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../supabase'
import type { Cols } from '../types/db-rows'
import { usePaymentStore } from '../stores/payment.store'
import type { CardPaymentData, PaymentMethod } from '../types/payment.types'
import type { ChargeIntent, CheckoutPayer } from '../payments/charge-result'
import {
  cardToCreatePaymentWire,
  chargeIntentFromCreatePayment,
  payerToCreatePaymentWire,
} from '../payments/charge-result'
import {
  optionalClientCheckoutOrigin,
  optionalClientCheckoutReturnPath,
} from '../payments/checkout-origin'
import { classifyPaymentFailure } from '../payments/classify-failure'

export { PAYMENT_ERROR_KEYS } from '../payments/codes'
// Platform-split Sentry entry: Metro resolves `sentry.web.ts` on web and
// `sentry.ts` on native, so each build carries exactly one SDK. The lazy
// `require('@sentry/react-native')` this replaced was static to Metro anyway —
// it did not defer anything, it just pinned the *native* SDK into the web
// bundle next to the @sentry/react build web actually initialises.
import { Sentry } from '../utils/sentry'
import { isExpectedNonFatalOperationalError } from '../errors/service-error'

/**
 * Normalize a payment-step error into Sentry telemetry. Card fields are never
 * included.
 */
function capturePaymentError(err: unknown) {
  try {
    if (isExpectedNonFatalOperationalError(err)) return
    Sentry.captureException(err, {
      tags: { payment_step: 'card_3ds' },
      fingerprint: ['payment', 'card_challenge'],
    })
  } catch {
    // Never let Sentry itself fail the payment flow
  }
}

function addPaymentBreadcrumb(category: string, message: string, data: Record<string, unknown>) {
  try {
    Sentry.addBreadcrumb({ category, level: 'info', message, data })
  } catch {
    // ignore
  }
}

/**
 * Map a machine-readable server `code` to an i18n key the user will actually
 * see. Without this the toast is the caller's generic fallback: errorMessage()
 * only surfaces a raw err.message when __DEV__, so in a release build every
 * distinct 409 collapsed into "Pago rechazado — usá otro método de pago".
 *
 * That generic advice is wrong for both: one means the price moved and the
 * other means the guest was charged and refunded, and "usá otro método de pago"
 * is bad counsel for each.
 *
 * An Error that survives the production toast path. `errorMessage()` consults
 * `userMessageKey` first and falls back to the caller's generic string for
 * anything else, so an error without a key cannot say anything specific.
 */
function paymentError(message: string, code: string | null): Error {
  const err = new Error(message) as Error & {
    userMessageKey?: string
    paymentCode?: string
    code?: string
  }
  if (code === 'BOOKING_TOO_SOON') {
    err.code = 'BOOKING_TOO_SOON'
    err.userMessageKey = 'rental_terms_flow.minimum_lead'
    return err
  }
  if (code === 'RENTAL_TERMS_CHANGED' || code === 'RENTAL_TERMS_NOT_ACCEPTED') {
    err.code = code
    err.userMessageKey = 'rental_terms_flow.changed'
    return err
  }
  if (code === 'HOST_TERMS_NOT_CURRENT') {
    err.code = code
    err.userMessageKey = 'rental_terms_flow.host_unavailable'
    return err
  }
  const classified = classifyPaymentFailure({ code, message })
  err.userMessageKey = classified.i18nKey
  err.paymentCode = classified.code
  return err
}

async function functionsErrorBody(
  error: unknown,
  fallback: string,
): Promise<{ message: string; code: string | null }> {
  const context = (error as { context?: unknown })?.context
  if (context && typeof context === 'object' && 'json' in context) {
    try {
      const body = (await (context as { json: () => Promise<unknown> }).json()) as {
        error?: unknown
        message?: unknown
        code?: unknown
        reasonCode?: unknown
      }
      const message = body.error ?? body.message
      // Se leen LOS DOS nombres. `create-payment` usa `code` para unos motivos y
      // `reasonCode` para otros, y mirar solo uno deja al otro sin mensaje.
      const rawCode = body.code ?? body.reasonCode
      const code = typeof rawCode === 'string' ? rawCode : null
      if (typeof message === 'string' && message.trim()) {
        return {
          message: message.trim(),
          code: code ?? classifyPaymentFailure({ message }).code,
        }
      }
      if (code) return { message: fallback, code }
    } catch {
      // Supabase context body may already be consumed. Fall through to SDK message.
    }
  }

  const message = error instanceof Error ? error.message : String(error || '')
  const classified = classifyPaymentFailure({ message })
  return {
    message:
      message && message !== 'Edge Function returned a non-2xx status code' ? message : fallback,
    code: classified.code === 'UNKNOWN' ? null : classified.code,
  }
}

async function functionsErrorMessage(error: unknown, fallback: string): Promise<string> {
  return (await functionsErrorBody(error, fallback)).message
}

// ─── Query Key Factory ─────────────────────────────────────────────────────

const paymentKeys = {
  all: ['payments'] as const,
  rentalPayments: (rentalId: string) => [...paymentKeys.all, 'rental', rentalId] as const,
  paymentMethods: () => [...paymentKeys.all, 'methods'] as const,
}

const PAYMENT_METHOD_SELECT =
  'id, profile_id, method_type, provider, metadata, label, card_brand, card_last4, card_exp_month, card_exp_year, billing_name, is_default, is_active, created_at, updated_at'

// ─── Row Mappers ───────────────────────────────────────────────────────────

/**
 * Shape returned by `PAYMENT_METHOD_SELECT`. The
 * `set_default_payment_method` / `deactivate_payment_method` RPCs return the
 * full `payment_methods` row, which is a superset, so both feed this mapper.
 */
type PaymentMethodRow = Omit<
  Cols<
    'payment_methods',
    | 'id'
    | 'profile_id'
    | 'method_type'
    | 'provider'
    | 'metadata'
    | 'label'
    | 'card_brand'
    | 'card_last4'
    | 'card_exp_month'
    | 'card_exp_year'
    | 'billing_name'
    | 'is_default'
    | 'is_active'
    | 'created_at'
    | 'updated_at'
  >,
  'metadata'
> & { metadata?: unknown }

/**
 * `save-payment-method` answers with an already-camelCased projection rather
 * than the raw row, so its contract is declared here and applied on `invoke`.
 */
interface EdgePaymentMethod {
  id: string
  profileId: string
  methodType: PaymentMethod['methodType']
  provider: string
  metadata?: Record<string, unknown>
  label: string
  cardBrand: string | null
  cardLast4: string | null
  cardExpMonth: number | null
  cardExpYear: number | null
  billingName: string | null
  isDefault: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

function mapPaymentMethodRow(row: PaymentMethodRow): PaymentMethod {
  return {
    id: row.id,
    profileId: row.profile_id,
    methodType: row.method_type,
    provider: row.provider,
    metadata:
      row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
        ? (row.metadata as Record<string, unknown>)
        : {},
    label: row.label ?? `${(row.card_brand ?? 'Card').toUpperCase()} •••• ${row.card_last4 ?? ''}`,
    cardBrand: row.card_brand,
    cardLast4: row.card_last4,
    cardExpMonth: row.card_exp_month,
    cardExpYear: row.card_exp_year,
    billingName: row.billing_name,
    isDefault: row.is_default,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

function mapEdgePaymentMethod(row: EdgePaymentMethod): PaymentMethod {
  return {
    id: row.id,
    profileId: row.profileId,
    methodType: row.methodType,
    provider: row.provider,
    metadata: row.metadata ?? {},
    label: row.label,
    cardBrand: row.cardBrand,
    cardLast4: row.cardLast4,
    cardExpMonth: row.cardExpMonth,
    cardExpYear: row.cardExpYear,
    billingName: row.billingName,
    isDefault: row.isDefault,
    isActive: row.isActive,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  }
}

async function invalidatePaymentMethods(queryClient: ReturnType<typeof useQueryClient>) {
  await queryClient.invalidateQueries({ queryKey: paymentKeys.paymentMethods() })
}

export function usePaymentMethods() {
  return useQuery({
    queryKey: paymentKeys.paymentMethods(),
    queryFn: async () => {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser()
      if (userError) throw userError
      if (!user) return []

      const { data, error } = await supabase
        .from('payment_methods')
        .select(PAYMENT_METHOD_SELECT)
        .eq('profile_id', user.id)
        .eq('method_type', 'card')
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []).map(mapPaymentMethodRow)
    },
  })
}

export function useSavePaymentMethod() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      card: CardPaymentData
      label?: string
      makeDefault?: boolean
    }) => {
      const { data, error } = await supabase.functions.invoke<{
        paymentMethod: EdgePaymentMethod
      }>('save-payment-method', {
        body: {
          card: cardToCreatePaymentWire(params.card),
          billingName: params.card.cardholderName,
          label: params.label,
          makeDefault: params.makeDefault,
        },
      })

      if (error) {
        throw new Error(await functionsErrorMessage(error, 'No pudimos guardar esta tarjeta.'))
      }

      if (!data?.paymentMethod) {
        throw new Error('No pudimos guardar esta tarjeta.')
      }
      return mapEdgePaymentMethod(data.paymentMethod)
    },
    onSuccess: async () => {
      await invalidatePaymentMethods(queryClient)
    },
  })
}

export function useSetDefaultPaymentMethod() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { data, error } = await supabase.rpc('set_default_payment_method', {
        p_payment_method_id: paymentMethodId,
      })
      if (error) throw error
      return mapPaymentMethodRow(data)
    },
    onSuccess: async () => {
      await invalidatePaymentMethods(queryClient)
    },
  })
}

export function useDeactivatePaymentMethod() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const { data, error } = await supabase.rpc('deactivate_payment_method', {
        p_payment_method_id: paymentMethodId,
      })
      if (error) throw error
      return mapPaymentMethodRow(data)
    },
    onSuccess: async () => {
      await invalidatePaymentMethods(queryClient)
    },
  })
}

/** Native card form → Direct 3DS — primary flow */
export function useProcessCardPayment() {
  const queryClient = useQueryClient()
  const { setStep, setError } = usePaymentStore()

  return useMutation({
    mutationFn: async (params: {
      rentalId: string
      card?: CardPaymentData
      paymentMethodId?: string
      cvv?: string
      payer: CheckoutPayer
    }): Promise<ChargeIntent> => {
      setStep('processing')
      // NEVER include card in breadcrumb data — logged to Sentry
      addPaymentBreadcrumb('payment', 'create-payment:challenge invoked', {
        rentalId: params.rentalId,
        mode: params.paymentMethodId ? 'saved_card' : 'new_card',
      })

      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: {
          rentalId: params.rentalId,
          ...(params.paymentMethodId
            ? { paymentMethodId: params.paymentMethodId, cvv: params.cvv }
            : params.card
              ? { card: cardToCreatePaymentWire(params.card) }
              : {}),
          payer: payerToCreatePaymentWire(params.payer),
          checkoutOrigin: optionalClientCheckoutOrigin(),
          checkoutReturnPath: optionalClientCheckoutReturnPath(),
        },
      })

      if (error) {
        const body = await functionsErrorBody(error, 'No pudimos iniciar el cobro.')
        throw paymentError(body.message, body.code)
      }

      return chargeIntentFromCreatePayment(data)
    },
    onSuccess: (_result, variables) => {
      setStep('idle')
      void queryClient.invalidateQueries({
        queryKey: paymentKeys.rentalPayments(variables.rentalId),
      })
    },
    onError: (error: Error) => {
      setStep('failed')
      setError(error.message)
      capturePaymentError(error)
    },
  })
}

/** PayPal hosted checkout → browser/App Switch → server-side capture. */
export function useProcessPayPalPayment() {
  const queryClient = useQueryClient()
  const { setStep, setError } = usePaymentStore()

  return useMutation({
    mutationFn: async (params: {
      rentalId: string
      payer: CheckoutPayer
      appSwitch?: { platform: 'ios' | 'android' | 'web'; osVersion?: string }
    }): Promise<ChargeIntent> => {
      setStep('processing')
      addPaymentBreadcrumb('payment', 'create-payment:paypal invoked', {
        rentalId: params.rentalId,
        provider: 'paypal',
        appSwitch: params.appSwitch?.platform ?? 'web',
      })

      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: {
          provider: 'paypal',
          rentalId: params.rentalId,
          payer: payerToCreatePaymentWire(params.payer),
          ...(params.appSwitch ? { appSwitch: params.appSwitch } : {}),
        },
      })

      if (error) {
        const body = await functionsErrorBody(error, 'No pudimos abrir el pago con PayPal.')
        throw paymentError(body.message, body.code)
      }

      const intent = chargeIntentFromCreatePayment(data)
      if (!intent.transactionId || !intent.paymentId || !intent.challenge?.url) {
        throw paymentError('No pudimos abrir el pago hospedado.', 'HOSTED_CHECKOUT_FAILED')
      }
      return intent
    },
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: paymentKeys.rentalPayments(variables.rentalId),
      })
    },
    onError: (error: Error) => {
      setStep('failed')
      setError(error.message)
      try {
        if (!isExpectedNonFatalOperationalError(error)) {
          Sentry.captureException(error, {
            tags: { payment_step: 'paypal_checkout' },
            fingerprint: ['paypal', 'checkout'],
          })
        }
      } catch {
        // Never let telemetry interrupt payment recovery.
      }
    },
  })
}
